import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button, Drawer, Position, Spinner } from '@blueprintjs/core';
import { fetchLeadDetail, type FieldFill } from './api';
import { leadImportKeys } from './keys';
import { queryPolicies } from '../../utils/queryPolicies';
import { ErrorNote, Pill, REASON_LABEL, VerifyCell, fmtDay, fmtMoney, fmtWhen } from './ui';
import { MerchantFromLeadDialog } from './MerchantFromLead';
import FieldFills from './FieldFills';
import RecordLink from '../links/RecordLink';
// Also opened from the merchant panel, outside the Imports page.
import './LeadImport.css';

// The summary card already shows the name, phone and email.
const FIELD_ORDER = ['first_name', 'last_name', 'alternate_phone', 'alternate_phone2', 'alternative_email',
  'street_num', 'street', 'city', 'state', 'zip', 'industry', 'monthly_revenue', 'annual_revenue', 'requesting', 'credit_score', 'ein', 'website',
  'date_business_started', 'ownership_percentage', 'balances', 'fax', 'contactmethod', 'marketingbranddb', 'note'];

interface Sighting {
  id: number; batch: number; source: string | null; sub_source: string | null; file_name: string; batch_date: string;
  is_master: boolean; cost_per_lead: number | null; current_rep: string | null;
}
interface Distribution { rep: string; at: string; lead: number; source: string | null; how: 'assigned' | 'redistributed' }
/** A rep who holds this lead now: the assigned rep (run null), then reps it was shared with by a redistribution run. */
interface Holder { rep: number; name: string; since: string | null; run: number | null }

/** Lead side panel. The top answers what an operator checks first (spec v4, E):
 * who the lead is, where it came from, who already had it and what each
 * earlier copy cost. The full record follows. The RPC is operator-only, so
 * cost and vendor never reach a rep. */
export default function LeadDrawer({ leadId, onClose, onOpenLead }: { leadId: number | null; onClose: () => void; onOpenLead: (id: number) => void }) {
  const q = useQuery({
    queryKey: leadImportKeys.lead(leadId ?? 0),
    queryFn: () => fetchLeadDetail(leadId as number),
    enabled: leadId != null,
    ...queryPolicies.recordDetail,
  });
  const [linking, setLinking] = useState(false);

  const d = q.data;
  const lead = d?.lead ?? {};
  const flags = d?.flags;
  const extra = lead.extra_data ?? {};
  const phoneMerchants: { id: number; label: string | null }[] = flags?.phone_merchants ?? [];
  const sightings: Sighting[] = d?.group ?? [];
  const self = sightings.find((g) => g.id === lead.id);
  const others = sightings.filter((g) => g.id !== lead.id);
  const distributions: Distribution[] = d?.distributed_to ?? [];
  const holders: Holder[] = d?.holders ?? [];
  const contact = [lead.first_name, lead.last_name].filter(Boolean).join(' ');
  const fills: FieldFill[] = d?.field_fills ?? [];

  const merchantLine = lead.merchant ? (
    <span className="li-nowrap">{lead.merchant_label || `Merchant #${lead.merchant}`} <RecordLink tableName="merchant" recordId={lead.merchant} /></span>
  ) : flags?.known_merchant && lead.merchant_link_status === 'linked' ? (
    <span className="li-muted">Linked to a merchant you do not have access to</span>
  ) : flags?.known_merchant ? (
    phoneMerchants.length === 0 ? <span className="li-muted">Phone belongs to a merchant you do not have access to</span> : (
      <span>Phone belongs to {phoneMerchants.map((m, i) => (
        <Fragment key={m.id}>{i > 0 && ', '}<span className="li-nowrap">{m.label || `merchant #${m.id}`} <RecordLink tableName="merchant" recordId={m.id} /></span></Fragment>
      ))}</span>
    )
  ) : <span className="li-muted">Not a merchant yet</span>;
  const canLink = !lead.merchant && !(flags?.known_merchant && lead.merchant_link_status === 'linked')
    && (!flags?.known_merchant || phoneMerchants.length > 0);

  return (
    <Drawer isOpen={leadId != null} onClose={onClose} position={Position.RIGHT} size="min(600px, 100vw)" title={lead.business_name || contact || lead.phone || 'Lead'} className="li-drawer">
      <div className="li-drawer__body" data-testid="record-detail">
        {q.isLoading && <Spinner size={24} />}
        <ErrorNote error={q.error} />
        {d && (
          <>
            <section className="li-lead-hero" aria-label="Lead summary">
              <div className="li-lead-hero__name">{lead.business_name || contact || 'No business name'}</div>
              {lead.business_name && contact && <div className="li-muted">{contact}</div>}
              <dl className="li-dl li-dl--tight">
                <dt>Phone</dt><dd>{lead.phone ? <a href={`tel:${lead.phone_normalized ?? lead.phone}`}>{lead.phone}</a> : '-'}</dd>
                <dt>Email</dt><dd>{lead.email ? <a href={`mailto:${lead.email}`}>{lead.email}</a> : <span className="li-muted">None</span>}</dd>
                <dt>Merchant</dt>
                <dd className="li-row li-wrap">
                  {merchantLine}
                  {canLink && (
                    <button type="button" className="li-btn li-btn--secondary li-btn--sm" onClick={() => setLinking(true)}>
                      {flags?.known_merchant ? 'Attach to merchant' : 'Make a merchant'}
                    </button>
                  )}
                </dd>
              </dl>
              <div className="li-row li-wrap">
                <Pill tone={flags?.eligible ? 'success' : 'muted'}>{flags?.eligible ? 'Assignable' : 'Not assignable'}</Pill>
                {(flags?.reasons ?? []).map((r: string) => <Pill key={r} tone="warn">{REASON_LABEL[r] ?? r}</Pill>)}
                <Pill tone={lead.master_lead ? 'info' : 'neutral'}>{lead.master_lead ? `Repeat of lead #${lead.master_lead}` : 'First time this phone was received'}</Pill>
              </div>
            </section>

            <section aria-label="Where this lead came from">
              <h4>Where this lead came from</h4>
              <dl className="li-dl">
                <dt>File</dt><dd>{d.batch?.file_name}</dd>
                <dt>Batch date</dt><dd>{fmtDay(d.batch?.batch_date)}</dd>
                <dt>Source</dt><dd>{d.batch?.source ?? '-'}{self?.sub_source ? ` / ${self.sub_source}` : ''}</dd>
                <dt>Cost per lead</dt><dd>{self?.cost_per_lead == null ? <span className="li-muted">No purchase linked</span> : fmtMoney(self.cost_per_lead)}</dd>
                <dt>Phone check</dt><dd>{lead.phone_verification_status ? <><VerifyCell status={lead.phone_verification_status} /> <span className="li-muted">{fmtWhen(lead.phone_verified_at)}</span></> : <span className="li-muted">Not checked</span>}</dd>
                <dt>Email check</dt><dd>{lead.email_verification_status ? <><VerifyCell status={lead.email_verification_status} /> <span className="li-muted">{fmtWhen(lead.email_verified_at)}</span></> : <span className="li-muted">Not checked</span>}</dd>
              </dl>
            </section>

            {fills.length > 0 && (
              <section aria-label="Values for the merchant">
                <h4>Values for the merchant</h4>
                <p className="li-muted">
                  {fills.some((f) => f.outcome === 'conflict')
                    ? 'Where the merchant already had a different value, it was kept and this lead\'s value is shown next to it.'
                    : 'Fields the merchant had empty, filled from this lead.'}
                </p>
                <FieldFills fills={fills} />
              </section>
            )}

            <section aria-label="Held by">
              <h4>Held by</h4>
              {holders.length === 0 ? <p className="li-muted">No rep holds this lead.</p> : (
                <ul className="li-timeline">
                  {holders.map((h) => (
                    <li key={h.rep}>
                      <b>{h.name}</b>
                      <span className="li-muted">{h.run == null ? 'assigned' : `shared by redistribution #${h.run}`}{h.since ? `, ${fmtWhen(h.since)}` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section aria-label="Previously distributed to">
              <h4>Previously distributed to</h4>
              {distributions.length === 0 ? <p className="li-muted">No rep has had this phone.</p> : (
                <ul className="li-timeline">
                  {distributions.map((x, i) => (
                    <li key={i}>
                      <b>{x.rep}</b>{x.how === 'redistributed' && <span className="li-subtag">redistributed</span>}
                      <span className="li-muted">{fmtWhen(x.at)}{x.lead !== lead.id ? `, from the ${x.source ?? 'other'} copy (#${x.lead})` : ''}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>

            <section aria-label="Other imports of this phone">
              <h4>Other imports of this phone ({others.length})</h4>
              {others.length === 0 ? <p className="li-muted">This phone has not come in any other import.</p> : (
                <table className="li-table li-table--compact">
                  <thead><tr><th>Source</th><th>File</th><th>Batch date</th><th className="r num">Cost per lead</th><th>Rep now</th></tr></thead>
                  <tbody>
                    {others.map((g) => (
                      <tr key={g.id}>
                        <td>{g.source ?? '-'}{g.sub_source ? <span className="li-muted"> / {g.sub_source}</span> : null} {g.is_master && <Pill tone="muted">first</Pill>}</td>
                        <td><Button small minimal onClick={() => onOpenLead(g.id)} title={`Open lead #${g.id}`}>{g.file_name}</Button></td>
                        <td>{fmtDay(g.batch_date)}</td>
                        <td className="r num">{g.cost_per_lead == null ? <span className="li-muted">-</span> : fmtMoney(g.cost_per_lead)}</td>
                        <td>{g.current_rep ?? <span className="li-muted">unassigned</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>

            <h4>All fields</h4>
            <dl className="li-dl li-dl--fields">
              {FIELD_ORDER.filter((f) => lead[f]).map((f) => (
                <Fragment key={f}><dt>{f.replace(/_/g, ' ')}</dt><dd>{lead[f]}</dd></Fragment>
              ))}
              <dt>phone (normalized)</dt><dd className="li-mono">{lead.phone_normalized}</dd>
              <dt>phone column</dt><dd>{lead.phone_source_column}</dd>
            </dl>

            {(extra.alternatives || extra.unmapped) && (
              <>
                <h4>Extra data and alternatives</h4>
                <dl className="li-dl">
                  {Object.entries(extra.alternatives ?? {}).map(([k, v]) => (
                    <Fragment key={`a-${k}`}><dt>{k} (other values)</dt><dd>{(v as { v: string }[]).map((x) => x.v).join(', ')}</dd></Fragment>
                  ))}
                  {Object.entries(extra.unmapped ?? {}).map(([k, v]) => (
                    <Fragment key={`u-${k}`}><dt>{k.replace(/#1$/, '')}</dt><dd>{(v as { v: string }[]).map((x) => x.v).join(', ')}</dd></Fragment>
                  ))}
                </dl>
              </>
            )}

            <h4>Phones in the file</h4>
            <table className="li-table li-table--compact">
              <thead><tr><th>Column</th><th>Value</th><th>Normalized</th><th /></tr></thead>
              <tbody>
                {d.phones.map((p: { source_header: string; raw_value: string; normalized: string | null; is_primary: boolean }, i: number) => (
                  <tr key={i}><td>{p.source_header}</td><td>{p.raw_value}</td><td className="li-mono">{p.normalized ?? <span className="li-muted">unusable</span>}</td>
                    <td>{p.is_primary && <Pill tone="info">primary</Pill>}</td></tr>
                ))}
              </tbody>
            </table>
            <p className="li-muted">Source rows: {d.origins.map((o: { sheet: string; row: number }) => `${o.sheet} row ${o.row}`).join(', ')}</p>

            {d.secondary_matches.length > 0 && (
              <>
                <h4>Email and EIN matches</h4>
                <p className="li-muted">Evidence only. It never links or blocks a lead.</p>
                <ul className="li-list">
                  {d.secondary_matches.map((m: { key_type: string; value: string; merchant_label: string; relation: string }, i: number) => (
                    <li key={i}>{m.key_type} <span className="li-mono">{m.value}</span> matches <b>{m.merchant_label}</b> <Pill tone={m.relation === 'agrees' ? 'success' : 'warn'}>{m.relation.replace('_', ' ')}</Pill></li>
                  ))}
                </ul>
              </>
            )}

            <h4>Assignment history of this copy</h4>
            {d.assignment_history.length === 0 ? <p className="li-muted">Never assigned.</p> : (
              <ul className="li-list">
                {d.assignment_history.map((e: { at: string; action: string; previous_rep: string | null; new_rep: string | null; reason: string | null }, i: number) => (
                  <li key={i}>{fmtWhen(e.at)}: {e.action.replace('_', ' ')} {e.previous_rep ? `from ${e.previous_rep} ` : ''}to {e.new_rep ?? 'nobody'}{e.reason ? ` (${e.reason})` : ''}</li>
                ))}
              </ul>
            )}

            {linking && (
              <MerchantFromLeadDialog lead={lead} source={d.batch?.source} canCreate={!flags?.known_merchant}
                suggested={phoneMerchants[0]} onClose={() => setLinking(false)} />
            )}
          </>
        )}
      </div>
    </Drawer>
  );
}
